'use client';

/**
 * Product Options Selector Component - Magento Style
 *
 * Matches production design with:
 * - Collapsible "Product Options and Configurations:" accordion
 * - Checkbox options with price modifiers
 * - Dropdown selects
 * - Qty input + Add to Cart + Talk to Expert buttons
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import Link from 'next/link';

// Types
interface ProductOptionValue {
  id: number;
  title: string;
  skuSuffix: string | null;
  priceModifier: string;
  priceType: string;
  sortOrder: number;
  isDefault: boolean;
}
interface ProductOption {
  id: number;
  title: string;
  type: string;
  isRequired: boolean;
  sortOrder: number;
  values: ProductOptionValue[];
}
interface ProductOptionsSelectorMagentoProps {
  productId: number;
  baseSku: string;
  basePrice: string;
  specialPrice: string | null;
  isSpecialActive: boolean;
  msrp: string | null;
  options: ProductOption[];
  onPriceChange?: (adjustedPrice: number) => void;
  showOptionsLink?: boolean;
  /** When true, Add to Cart is disabled (e.g. a no-backorder item that is out of stock). */
  purchaseBlocked?: boolean;
}

// Selection state type
type OptionSelections = Record<number, number | number[] | null>;

// Format price for display
function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price);
}
export function ProductOptionsSelectorMagento({
  productId,
  basePrice,
  specialPrice,
  isSpecialActive,
  options,
  onPriceChange,
  showOptionsLink = false,
  purchaseBlocked = false
}: ProductOptionsSelectorMagentoProps) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [selections, setSelections] = useState<OptionSelections>(() => {
    const init: OptionSelections = {};
    for (const option of options) {
      const defaultValues = option.values.filter(v => v.isDefault).map(v_0 => v_0.id);
      if (defaultValues.length === 0) continue;
      if (option.type === 'checkbox') {
        init[option.id] = defaultValues;
      } else {
        // drop_down / dropdown — take first default value
        init[option.id] = defaultValues[0] ?? null;
      }
    }
    return init;
  });
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  // Check if all required options are selected
  const hasAllRequired = useMemo(() => {
    for (const option_0 of options) {
      if (option_0.isRequired) {
        const selection = selections[option_0.id];
        if (selection === null || selection === undefined) return false;
        if (Array.isArray(selection) && selection.length === 0) return false;
      }
    }
    return true;
  }, [selections, options]);

  // Calculate the adjusted price based on selected options
  const adjustedPrice = useMemo(() => {
    const basePriceNum = parseFloat(basePrice);
    const startingPrice = isSpecialActive && specialPrice ? parseFloat(specialPrice) : basePriceNum;
    let optionTotal = 0;
    for (const option_1 of options) {
      const selection_0 = selections[option_1.id];
      if (selection_0 === null || selection_0 === undefined) continue;
      const selectedIds = Array.isArray(selection_0) ? selection_0 : [selection_0];
      for (const valueId of selectedIds) {
        const value = option_1.values.find(v_1 => v_1.id === valueId);
        if (!value) continue;
        const modifier = parseFloat(value.priceModifier);
        if (isNaN(modifier) || modifier === 0) continue;
        if (value.priceType === 'percent') {
          // Percent modifiers use basePrice as basis (matching cart.service.ts)
          optionTotal += basePriceNum * (modifier / 100);
        } else {
          optionTotal += modifier;
        }
      }
    }
    return startingPrice + optionTotal;
  }, [basePrice, specialPrice, isSpecialActive, options, selections]);

  // Notify parent of price changes
  useEffect(() => {
    onPriceChange?.(adjustedPrice);
  }, [adjustedPrice, onPriceChange]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((optionId: number, valueId_0: number) => {
    setSelections(prev => {
      const current = prev[optionId] as number[] | null || [];
      const exists = current.includes(valueId_0);
      return {
        ...prev,
        [optionId]: exists ? current.filter(id => id !== valueId_0) : [...current, valueId_0]
      };
    });
  }, []);

  // Handle dropdown change
  const handleDropdownChange = useCallback((optionId_0: number, valueId_1: number | null) => {
    setSelections(prev_0 => ({
      ...prev_0,
      [optionId_0]: valueId_1
    }));
  }, []);

  // Get or create session ID
  const getSessionId = (): string => {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? `session-${crypto.randomUUID()}` : 'session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  };

  // Handle add to cart
  const handleAddToCart = useCallback(async () => {
    if (!hasAllRequired) {
      toast.error('Please select all required options');
      return;
    }
    setAdding(true);
    try {
      const selectedOptions = Object.entries(selections).flatMap(([optionId_1, selection_1]) => {
        if (selection_1 === null || selection_1 === undefined) return [];
        const selectedIds_0 = Array.isArray(selection_1) ? selection_1 : [selection_1];
        return selectedIds_0.map(valueId_2 => ({
          optionId: parseInt(optionId_1, 10),
          valueId: valueId_2
        }));
      });
      const sessionId_0 = getSessionId();
      window.dispatchEvent(new CustomEvent('cart-adding'));
      const response = await fetch('/api/cart/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: sessionId_0,
          productId,
          quantity,
          selectedOptions
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to add to cart');
      }
      window.dispatchEvent(new CustomEvent('cart-updated'));
      setQuantity(1);
    } catch (error) {
      toast.error('Failed to add to cart', {
        description: error instanceof Error ? error.message : 'Please try again'
      });
    } finally {
      setAdding(false);
    }
  }, [hasAllRequired, selections, productId, quantity]);

  // Separate options by type
  const checkboxOptions = options.filter(o => o.type === 'checkbox');
  const dropdownOptions = options.filter(o_0 => o_0.type === 'dropdown' || o_0.type === 'drop_down');
  return <div>
      {/* Product Options Accordion */}
      {options.length > 0 && <div className={`product-options-accordion ${isAccordionOpen ? 'open' : ''}`}>
          <div className="product-options-header" onClick={() => setIsAccordionOpen(!isAccordionOpen)}>
            <h3>Product Options and Configurations:</h3>
            <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </div>

          <div className="product-options-content">
            {/* Checkbox Options */}
            {checkboxOptions.map(option_2 => <div key={option_2.id} className="option-group">
                <div className="option-label">{option_2.title}</div>
                {option_2.values.map(value_0 => {
            const price = parseFloat(value_0.priceModifier);
            const selectedValues = selections[option_2.id] as number[] | null || [];
            const isSelected = selectedValues.includes(value_0.id);
            return <div key={value_0.id} className="option-checkbox-item">
                      <input type="checkbox" id={`option-${option_2.id}-${value_0.id}`} checked={isSelected} onChange={() => handleCheckboxChange(option_2.id, value_0.id)} />
                      <label htmlFor={`option-${option_2.id}-${value_0.id}`}>
                        {value_0.title}
                        {price > 0 && <span className="price-addon"> + {formatPrice(price)}</span>}
                      </label>
                    </div>;
          })}
              </div>)}

            {/* Dropdown Options */}
            {dropdownOptions.map(option_3 => <div key={option_3.id} className="option-group">
                <div className="option-label">{option_3.title}</div>
                <select className="option-select" value={selections[option_3.id] as number | '' ?? ''} onChange={e => {
            const val = e.target.value ? parseInt(e.target.value, 10) : null;
            handleDropdownChange(option_3.id, val);
          }}>
                  <option value="">none</option>
                  {option_3.values.map(value_1 => {
              const price_0 = parseFloat(value_1.priceModifier);
              const priceStr = price_0 > 0 ? ` (+${formatPrice(price_0)})` : '';
              return <option key={value_1.id} value={value_1.id}>
                        {value_1.title}{priceStr}
                      </option>;
            })}
                </select>
              </div>)}

            {/* View product options details link - scrolls to visual options block */}
            {showOptionsLink && <a href="#product-options-visual" className="view-options-link">
                View product options details
              </a>}
          </div>
        </div>}

      {/* Add to Cart Section */}
      <div className="add-to-cart-section">
        {/* Qty */}
        <div className="qty-wrapper">
          <label htmlFor="qty">Qty :</label>
          <input type="number" id="qty" className="qty-input" min="1" value={quantity} onChange={e_0 => setQuantity(Math.max(1, parseInt(e_0.target.value) || 1))} />
        </div>

        {/* Add to Cart Button */}
        <button type="button" className="btn-add-to-cart" disabled={!hasAllRequired || adding || purchaseBlocked} onClick={handleAddToCart}>
          <CartIcon />
          {adding ? 'Adding...' : 'Add to Cart'}
        </button>

        {/* Talk to Expert Button */}
        <Link href="/contact-us" className="btn-talk-expert">
          TALK TO AN<br />EXPERT
        </Link>
      </div>

      {/* Required options message */}
      {!hasAllRequired && options.some(o_1 => o_1.isRequired) && <p className="mt-3 text-sm text-amber-600">
          * Please select all required options to add to cart
        </p>}
    </div>;
}
function CartIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}